import type { Integration } from '@botpress/client'
import { ArrowRight } from 'lucide-react'
import React, { ComponentProps, PropsWithChildren } from 'react'
import { useUpdateBot } from '../../../hooks'
import { BotSelector } from './BotSelector'
import { SuccessCheckmark } from './SuccessCheckmark'
import { BotSummary } from '~/features/bots/types'
import { openConfigureBotIntegrationStudioTab } from '~/features/integrations/services/utils'
import { Popover, PopoverTrigger, PopoverContent, Button } from '@bpinternal/ui-kit-next'
import { Spinner } from '~/elements'

type BotSelectorPopoverProps = {
  currentIntegration: Integration
  workspaceId: string
} & PropsWithChildren<ComponentProps<'div'>>

export const PrivateIntegrationInstallPopover = ({
  children,
  currentIntegration,
  workspaceId,
  ...props
}: BotSelectorPopoverProps) => {
  const [selectedBot, setSelectedBot] = React.useState<BotSummary>()
  const [formState, setFormState] = React.useState<'bot' | 'done'>('bot')

  const { mutate: updateBot, isPending: isUpdatingBot } = useUpdateBot()

  const addIntegration = () => {
    if (!selectedBot) {
      return
    }

    setFormState('done')

    updateBot({
      workspaceId,
      id: selectedBot.id,
      integrations: {
        [currentIntegration.id]: {
          enabled: false,
          configuration: {},
        },
      },
      onError: () => {
        setFormState('bot')
      },
    })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-[28rem] bg-neutral-50">
        <div {...props} className="flex flex-col p-4">
          {formState === 'bot' && (
            <>
              <BotSelector
                selectedBot={selectedBot}
                currentIntegration={currentIntegration}
                currentWorkspaceId={workspaceId}
                setSelectedBot={setSelectedBot}
              />
              <Button size="sm" disabled={!selectedBot} className="w-fit self-end" onClick={addIntegration}>
                Add to bot
              </Button>
            </>
          )}
          {formState === 'done' &&
            (isUpdatingBot ? (
              <Spinner className="h-8 w-8 self-center" />
            ) : (
              <>
                <div className="my-4 mb-6 h-12 w-12 self-center">
                  <SuccessCheckmark />
                </div>
                <h3 className="mb-2 self-center font-semibold text-neutral-950">Integration successfully installed!</h3>
                <p className="mb-12 self-center text-sm text-neutral-900">
                  You can now go back to the dashboard and configure it.
                </p>
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => openConfigureBotIntegrationStudioTab(selectedBot?.id ?? '', currentIntegration.id)}
                    size="sm"
                  >
                    Configure Integration
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </>
            ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
